import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import type { ImageSourcePropType } from 'react-native';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import {
  BenefitCard,
  getContentfulImageUrl,
  theme,
  useResponsive,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassBenefitCard = (
  props: SweetpassLandingPageBenefitsCardProps,
) => {
  const { title, text, imgUrl, imgA11yLabel, withTopBorder, isUnavailable } =
    props;

  const { match } = useResponsive();
  const { scale } = useWindowDimensions();

  // ─── Illustration ────────────────────────────────────────────────────

  const displayScale = Math.ceil(scale);

  const illustrationSize = match(BENEFIT_ILLUSTRATION_RESPONSIVE_SIZES);
  const illustrationScaledSize = illustrationSize * displayScale;

  const imgSource = useMemo<ImageSourcePropType | undefined>(() => {
    if (!imgUrl) return;

    return {
      uri: getContentfulImageUrl(imgUrl, {
        fm: 'webp',
        q: 90,
        w: illustrationScaledSize,
        h: illustrationScaledSize,
      }),
    };
  }, [illustrationScaledSize, imgUrl]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    withTopBorder ? styles.containerWithTopBorder : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <BenefitCard
        title={title}
        text={text}
        imgA11yLabel={imgA11yLabel}
        imgSource={imgSource}
        isUnavailable={isUnavailable}
      />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BENEFIT_ILLUSTRATION_RESPONSIVE_SIZES = [80, 120, 200];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
  },
  containerWithTopBorder: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassLandingPageBenefitsCardProps = Omit<
  ComponentProps<typeof BenefitCard>,
  'imgSource'
> &
  Readonly<{
    imgUrl?: string;
    withTopBorder?: boolean;
  }>;
